import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding, resolveLogoUrl } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, ShieldCheck, Package } from "lucide-react";

export default function RequesterLogin() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(username.trim(), password);
    setSubmitting(false);
    if (!res.ok) { toast.error(res.error); return; }
    if (res.user.role !== "solicitante") {
      toast.error("Esta tela é apenas para solicitantes. Use /admin/login para o administrador.");
      return;
    }
    toast.success(`Olá, ${res.user.nome || res.user.username}`);
    nav("/portal");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(160deg, var(--brand-accent), #1e293b)" }}>
        <div className="flex items-center gap-3 text-white">
          {resolveLogoUrl(branding.logo_url) ? (
            <img src={resolveLogoUrl(branding.logo_url)} alt="logo" className="h-10 w-10 rounded object-cover bg-white/10" />
          ) : (
            <div className="h-10 w-10 rounded flex items-center justify-center border border-white/30">
              <Package className="h-5 w-5" />
            </div>
          )}
          <span className="font-heading text-lg font-bold">{branding.company_name}</span>
        </div>
        <div className="text-white space-y-4 relative z-10">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            {branding.welcome_text}
          </h1>
          <p className="text-slate-300 max-w-md">Faça login para registrar novas solicitações e acompanhar seu histórico de pedidos.</p>
        </div>
        <div className="text-slate-400 text-xs">© {new Date().getFullYear()} {branding.company_name}</div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full" style={{ background: "var(--brand-primary)", opacity: 0.15 }} />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {resolveLogoUrl(branding.logo_url) ? (
              <img src={resolveLogoUrl(branding.logo_url)} alt="logo" className="h-10 w-10 rounded object-cover" />
            ) : (
              <div className="h-10 w-10 rounded bg-brand text-white flex items-center justify-center">
                <Package className="h-5 w-5" />
              </div>
            )}
            <span className="font-heading text-lg font-bold">{branding.company_name}</span>
          </div>

          <h2 className="font-heading text-3xl font-bold text-slate-900">Acesso do solicitante</h2>
          <p className="text-slate-500 mt-2 mb-8">Entre com o usuário e senha fornecidos pelo administrador.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Usuário</Label>
              <Input
                id="username"
                data-testid="req-username-input"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="seu.usuario"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                data-testid="req-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="req-login-submit"
              disabled={submitting}
              className="w-full btn-brand rounded-full h-11"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {submitting ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between text-sm">
            <span className="text-slate-500">É administrador?</span>
            <Link
              to="/admin/login"
              data-testid="go-admin-login"
              className="inline-flex items-center gap-2 text-slate-900 font-medium hover:text-brand transition-colors"
            >
              <ShieldCheck className="h-4 w-4" />
              Acessar painel admin
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
